import { Injectable } from '@angular/core';
import * as Blockly from 'blockly';

type BlocklyCopyableSelection = {
  workspace?: Blockly.WorkspaceSvg;
  getRelativeToSurfaceXY?: () => { x: number; y: number };
  toCopyData: () => Blockly.ICopyData | null;
};

type PersistedBlocklyClipboard = {
  version: 1;
  copiedAt: string;
  copyData: Blockly.ICopyData;
};

@Injectable({
  providedIn: 'root'
})
export class BlocklyClipboardService {
  private readonly storageKey = 'blockly.clipboard.v1';

  public registerWorkspace(workspace: Blockly.WorkspaceSvg): () => void {
    this.hydrateClipboard(workspace);

    const handleKeyDown = (event: KeyboardEvent) => {
      this.handleShortcut(workspace, event);
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }

  public clear(): void {
    localStorage.removeItem(this.storageKey);
  }

  private handleShortcut(workspace: Blockly.WorkspaceSvg, event: KeyboardEvent): void {
    if (!this.isClipboardShortcut(event)) {
      return;
    }

    if (!this.isWorkspaceShortcutTarget(workspace, event)) {
      return;
    }

    const key = event.key.toLowerCase();
    if (key === 'c' && this.copySelection(workspace)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (key === 'x') {
      this.cutSelection(workspace);
      return;
    }

    if (key === 'v' && this.pasteSelection(workspace)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private hydrateClipboard(workspace: Blockly.WorkspaceSvg | null): boolean {
    const persisted = this.getPersistedCopyData();
    if (!persisted) {
      return false;
    }

    Blockly.clipboard.setLastCopiedData(persisted);
    if (workspace) {
      Blockly.clipboard.setLastCopiedWorkspace(workspace);
    }
    return true;
  }

  private copySelection(workspace: Blockly.WorkspaceSvg): boolean {
    const selected = Blockly.getSelected() as BlocklyCopyableSelection | null;
    if (!selected || typeof selected.toCopyData !== 'function') {
      return false;
    }

    const copyData = selected.toCopyData();
    if (!copyData) {
      return false;
    }

    Blockly.clipboard.setLastCopiedData(copyData);
    Blockly.clipboard.setLastCopiedWorkspace(selected.workspace ?? workspace);

    const copiedLocation = selected.getRelativeToSurfaceXY?.();
    if (copiedLocation) {
      Blockly.clipboard.setLastCopiedLocation(new Blockly.utils.Coordinate(copiedLocation.x, copiedLocation.y));
    }

    this.persistCopyData(copyData);
    return true;
  }

  private pasteSelection(workspace: Blockly.WorkspaceSvg): boolean {
    const persisted = this.getPersistedCopyData();
    if (!persisted) {
      return false;
    }

    Blockly.clipboard.setLastCopiedData(persisted);
    Blockly.clipboard.setLastCopiedWorkspace(workspace);
    return !!Blockly.clipboard.paste(persisted, workspace);
  }

  private cutSelection(workspace: Blockly.WorkspaceSvg): boolean {
    const selected = Blockly.getSelected() as BlocklyCopyableSelection | null;
    if (!selected || typeof selected.toCopyData !== 'function') {
      return false;
    }

    const copyData = selected.toCopyData();
    if (!copyData) {
      return false;
    }

    Blockly.clipboard.setLastCopiedData(copyData);
    Blockly.clipboard.setLastCopiedWorkspace(selected.workspace ?? workspace);

    const copiedLocation = selected.getRelativeToSurfaceXY?.();
    if (copiedLocation) {
      Blockly.clipboard.setLastCopiedLocation(new Blockly.utils.Coordinate(copiedLocation.x, copiedLocation.y));
    }

    this.persistCopyData(copyData);
    return true;
  }

  private isClipboardShortcut(event: KeyboardEvent): boolean {
    if ((!event.ctrlKey && !event.metaKey) || event.altKey) {
      return false;
    }

    const key = event.key.toLowerCase();
    return key === 'c' || key === 'v' || key === 'x';
  }

  private isWorkspaceShortcutTarget(workspace: Blockly.WorkspaceSvg, event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, [contenteditable="true"]')) {
      return false;
    }

    const injectionDiv = workspace.getInjectionDiv();
    return injectionDiv.contains(target) || Blockly.getMainWorkspace() === workspace;
  }

  private persistCopyData(copyData: Blockly.ICopyData): void {
    const payload: PersistedBlocklyClipboard = {
      version: 1,
      copiedAt: new Date().toISOString(),
      copyData
    };

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch (err) {
      console.warn('Blockly clipboard konnte nicht persistiert werden.', err);
    }
  }

  private getPersistedCopyData(): Blockly.ICopyData | null {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<PersistedBlocklyClipboard>;
      if (parsed.version !== 1 || !parsed.copyData || typeof parsed.copyData !== 'object') {
        return null;
      }
      return parsed.copyData;
    } catch {
      return null;
    }
  }
}